import type { ContractHandlers } from '@cognitoforms/builder-types';
import type { Contract } from '@cognitoforms/types/contract-vocabulary';
import type { LegacyHandlers } from '../legacy-mirrored-builder';
import type { LegacyForm } from '../legacy-form';
import type { LookupFormSettings } from '@cognitoforms/types/server-types/core/model/forms/definition/lookup-form-settings';
import { PersonSettingsType } from '@cognitoforms/types/server-types/core/model/forms/definition/person-settings-type';
import { PeopleLinkingMode } from '@cognitoforms/types/server-types/forms/model/people-linking-mode';
import { FormType } from '@cognitoforms/types/server-types/forms/model/form-type';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare const Cognito: any;

type SettingsHandlers = Pick<LegacyHandlers, 'updateFormSettings' | 'updatePaymentSettings' | 'updateWorkflow'>;
type UpdateFormSettingsArgs = Parameters<ContractHandlers['updateFormSettings']>[0];
type UpdatePaymentSettingsArgs = Parameters<ContractHandlers['updatePaymentSettings']>[0];

type SubmitterSettings = Contract.UpdateSubmitterTrackingSettings | Contract.UpdatePersonFieldSubmitterSettings;

const PERSON_FORM_TYPES: string[] = [PersonSettingsType.User, PersonSettingsType.Guest, PersonSettingsType.Contact];

const applyPeopleForm = (legacyForm: LegacyForm, peopleForm: Contract.UpdateMappedPeopleFormSettings) => {
	const settings = legacyForm.get_PeopleFormSettings() ?? Cognito.deserialize(Cognito.Forms.PeopleFormSettings, {});
	const guest = peopleForm as Contract.UpdateGuestPeopleFormSettings;
	const user = peopleForm as Contract.UpdateUserPeopleFormSettings;

	if ('PersonType' in peopleForm)
		settings.set_PersonType(Cognito.getEnumWithName(Cognito.Forms.PersonFormType, peopleForm.PersonType));
	if (peopleForm.PersonType === PersonSettingsType.None) {
		settings.set_Name(null);
		settings.set_Email(null);
		settings.set_Phone(null);
		settings.set_Address(null);
		settings.set_Avatar(null);
		settings.set_IsActive(null);
		settings.set_IsGuestList(false);
		settings.set_InviteUsers(false);
		settings.set_GuestRegistration(false);
		settings.set_GuestProfileEditing(false);
	}
	if ('Name' in peopleForm) settings.set_Name(peopleForm.Name ?? null);
	if ('Email' in peopleForm) settings.set_Email(peopleForm.Email ?? null);
	if ('Phone' in peopleForm) settings.set_Phone(peopleForm.Phone ?? null);
	if ('Address' in peopleForm) settings.set_Address(peopleForm.Address ?? null);
	if ('Avatar' in peopleForm) settings.set_Avatar(peopleForm.Avatar ?? null);
	if ('IsActive' in peopleForm) settings.set_IsActive(peopleForm.IsActive ?? null);
	if ('IsGuestList' in guest) settings.set_IsGuestList(guest.IsGuestList);
	if ('InviteUsers' in user) settings.set_InviteUsers(user.InviteUsers);
	if ('GuestRegistration' in guest) settings.set_GuestRegistration(guest.GuestRegistration);
	if ('GuestProfileEditing' in guest) settings.set_GuestProfileEditing(guest.GuestProfileEditing);

	legacyForm.set_PeopleFormSettings(settings);
};

const applySubmitterTracking = (legacyForm: LegacyForm, people: SubmitterSettings) => {
	const settings = legacyForm.get_SubmitterPersonSettings() ?? Cognito.deserialize(Cognito.Forms.SubmitterPersonSettings, {});
	const mapped = people as Contract.UpdateSubmitterTrackingSettings;
	const personField = people as Contract.UpdatePersonFieldSubmitterSettings;

	settings.set_LinkingMode(Cognito.getEnumWithName(
		Cognito.Forms.PeopleLinkingMode,
		people.PersonType === PersonSettingsType.SubmitterPersonField ? PeopleLinkingMode.PersonField : PeopleLinkingMode.FieldMapping
	));

	if ('Name' in mapped) settings.set_NameFieldPath(mapped.Name ?? null);
	if ('Email' in mapped) settings.set_EmailFieldPath(mapped.Email ?? null);
	if ('Phone' in mapped) settings.set_PhoneFieldPath(mapped.Phone ?? null);
	if ('Address' in mapped) settings.set_AddressFieldPath(mapped.Address ?? null);
	if ('PersonFieldPath' in personField) settings.set_PersonFieldPath(personField.PersonFieldPath ?? null);
	if ('TargetPeopleFormId' in people) settings.set_TargetPeopleFormId(people.TargetPeopleFormId ?? null);
	if ('AutoCreateActionId' in people) settings.set_AutoCreateActionId(people.AutoCreateActionId ?? null);
	if ('CreateNewPersonEntries' in people) settings.set_CreateNewPersonEntries(people.CreateNewPersonEntries);
	if ('AutoConfigSuppressed' in personField) settings.set_AutoConfigSuppressed(personField.AutoConfigSuppressed);
	if ('SetupPending' in personField) settings.set_SetupPending(personField.SetupPending);

	legacyForm.set_SubmitterPersonSettings(settings);
};

const applyPersonSettings = (legacyForm: LegacyForm, people: Contract.AnyUpdatePersonSettings) => {
	if (people.PersonType === PersonSettingsType.Submitter || people.PersonType === PersonSettingsType.SubmitterPersonField) {
		const peopleFormSettings = Cognito.deserialize(Cognito.Forms.PeopleFormSettings, {});
		peopleFormSettings.set_PersonType(Cognito.getEnumWithName(Cognito.Forms.PersonFormType, PersonSettingsType.None));
		legacyForm.set_PeopleFormSettings(peopleFormSettings);
		legacyForm.set_FormType(Cognito.getEnumWithName(Cognito.Forms.FormType, FormType.Standard));
		applySubmitterTracking(legacyForm, people);
		return;
	}

	applyPeopleForm(legacyForm, people as Contract.UpdateMappedPeopleFormSettings);
	legacyForm.set_SubmitterPersonSettings(null);

	// FormType is the other half of this discriminator, so a person variant settles it.
	if (PERSON_FORM_TYPES.includes(people.PersonType as string))
		legacyForm.set_FormType(Cognito.getEnumWithName(Cognito.Forms.FormType, FormType.Person));
};

// The new model nests this under Settings.LookupForm; the legacy form exposes it as a flat
// property (form.get/set_SecureLookupsForGuests), same as identify-submitter.ts's usage.
const applyLookupForm = (legacyForm: LegacyForm, lookupForm: Partial<LookupFormSettings>) => {
	if ('PreventLookup' in lookupForm)
		legacyForm.set_PreventLookup(lookupForm.PreventLookup);
	if ('SecureLookupsForGuests' in lookupForm)
		legacyForm.set_SecureLookupsForGuests(lookupForm.SecureLookupsForGuests);
	if ('SecureSettingsChoiceDialogShown' in lookupForm)
		legacyForm.set_SecureSettingsChoiceDialogShown(lookupForm.SecureSettingsChoiceDialogShown);
	if ('HasLookups' in lookupForm)
		legacyForm.set_HasLookups(lookupForm.HasLookups);
	if ('HasV2Lookups' in lookupForm)
		legacyForm.set_HasV2Lookups(lookupForm.HasV2Lookups);
	if (lookupForm.EntrySummary !== undefined) {
		legacyForm.set_Format(lookupForm.EntrySummary);
		if (lookupForm.EntrySummaryCustomized === undefined)
			legacyForm.set_EntrySummaryCustomized(lookupForm.EntrySummary !== null);
	}
	if (lookupForm.EntrySummaryCustomized !== undefined)
		legacyForm.set_EntrySummaryCustomized(lookupForm.EntrySummaryCustomized);
};

export const createLegacySettingsHandlers = (): SettingsHandlers => ({
	updateFormSettings: (legacyForm: LegacyForm, args: UpdateFormSettingsArgs) => {
		if ('Name' in args)
			legacyForm.set_Name(args.Name);
		if ('Title' in args)
			legacyForm.set_Title(args.Title);
		if ('UrlName' in args)
			legacyForm.set_UrlName(args.UrlName);
		if ('Description' in args)
			legacyForm.set_Description(args.Description);
		if ('FormType' in args)
			legacyForm.set_FormType(Cognito.getEnumWithName(Cognito.Forms.FormType, args.FormType));
		if (args.People)
			applyPersonSettings(legacyForm, args.People);
		if (args.LookupForm)
			applyLookupForm(legacyForm, args.LookupForm);

		legacyForm.set_HasChanges(true);
	},

	updatePaymentSettings: (legacyForm: LegacyForm, args: UpdatePaymentSettingsArgs) => {
		if ('SameAsSubmitter' in args)
			legacyForm.get_BillingFieldSettings()?.set_SameAsSubmitter(args.SameAsSubmitter);

		legacyForm.set_HasChanges(true);
	}

	// updateWorkflow: add as that area moves to the new model.
});
